import * as Sentry from '@sentry/react';
import type { IPeerConnectionFactory } from '@playcastdotio/PeerCore';
import {
  GuestPeerSession,
  SdpInspector,
  IceHealthGuard,
  type GuestPeerSessionCallbacks,
  type GuestRetryPolicy,
  type GuestConnectOptions,
  type BeaconE2eCongestionLevel,
  type BeaconE2eSnapshot,
  type EncodedFrameTap,
  type GuestStatsSample,
  type InputPhotonSummary,
  type RvfcVideoElement,
  type VideoPresentationTarget,
  type VisibilitySource,
  type WcWorkerPort,
} from '@playcastdotio/PeerCore';
import type { GuestBrowserCapability } from '@playcastdotio/PeerCore';
import type { PeerMetadata, SignalingGenerationV1 } from '@playcastdotio/Messaging';
import {
  GuestWebRtcPolicyManager,
} from '../../features/webrtc-policy/GuestWebRtcPolicyManager';

export type RetryPolicy = GuestRetryPolicy;
export type ClientPeerCallbacks = GuestPeerSessionCallbacks;
export type { GuestConnectOptions };

/**
 * ClientPeerManager — thin marketing-demo wrapper around PeerCore GuestPeerSession.
 * Keeps the existing public API while shared guest media logic lives in PeerCore.
 */
export class ClientPeerManager {
  private readonly session: GuestPeerSession;
  private readonly mediaPolicy: GuestWebRtcPolicyManager;
  private readonly callbacks: ClientPeerCallbacks;

  /**
   * @param visibilitySource Where "is this page in front of the user" comes from. Optional so
   * every existing caller and every test keeps today's behaviour; supplied by the app so the
   * recovery budget actually stops while the tab is backgrounded, which is the whole point of
   * the fix — an option nothing ever passes is a fix that ships dead.
   */
  constructor(
    factory: IPeerConnectionFactory,
    callbacks: ClientPeerCallbacks,
    retryPolicy?: RetryPolicy,
    visibilitySource?: VisibilitySource,
    options: {
      capability?: GuestBrowserCapability;
      isMobile?: boolean;
      encodedFrameTap?: EncodedFrameTap;
      createWebCodecsWorker?: () => WcWorkerPort;
      mediaPolicy?: GuestWebRtcPolicyManager;
    } = {},
  ) {
    this.mediaPolicy = options.mediaPolicy ?? new GuestWebRtcPolicyManager();
    this.callbacks = callbacks;
    const optimizationManager = this.mediaPolicy.optimizationManager;
    this.session = new GuestPeerSession({
      factory,
      callbacks,
      retryPolicy,
      ...(visibilitySource ? { visibilitySource } : {}),
      ...(options.capability ? { capability: options.capability } : {}),
      ...(options.isMobile !== undefined ? { isMobile: options.isMobile } : {}),
      ...(options.encodedFrameTap ? { encodedFrameTap: options.encodedFrameTap } : {}),
      ...(options.createWebCodecsWorker ? { createWebCodecsWorker: options.createWebCodecsWorker } : {}),
      optimizationManager,
      mediaPolicyLifecycle: this.mediaPolicy.lifecycle,
      sdpInspector: new SdpInspector(),
      iceHealth: IceHealthGuard,
      logger: {
        // Block body, not a concise one: an expression-bodied arrow *returns* the
        // console.log result, so esbuild cannot elide it under the `pure` annotation in
        // vite.config.ts and the informational channel would still run in production.
        // `warn`/`error` are deliberately left as-is — they are retained by design.
        info: (message) => { console.log(`[ClientPeerManager] ${message}`); },
        warn: (message) => console.warn(`[ClientPeerManager] ${message}`),
        error: (message, data) => console.error(`[ClientPeerManager] ${message}`, data ?? ''),
        breadcrumb: (message, level, data) => {
          Sentry.addBreadcrumb({
            category: 'webrtc',
            message,
            level,
            data,
          });
        },
      },
      enableBeaconE2eHooks: true,
    });
  }

  public preWarm(iceServers: RTCIceServer[]): void {
    this.session.preWarm(iceServers);
  }

  public hasPreWarmedConnection(): boolean {
    return this.session.hasPreWarmedConnection();
  }

  public getMediaPolicyManager(): GuestWebRtcPolicyManager {
    return this.mediaPolicy;
  }

  public connect(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
    connectOptions?: GuestConnectOptions,
  ): void {
    void this.connectAsync(hostPeerId, iceServers, metadata, connectOptions).catch((error) => {
      console.error('[ClientPeerManager] Connect failed:', error);
    });
  }

  /**
   * Async connect that awaits the guest decode readiness barrier before offer/answer.
   *
   * A terminal media-policy refusal (the probe ANSWERED that no hardware decoder is
   * qualified) is reported to the app as a terminal, non-retryable disconnect, so the UI
   * leaves the loading state instead of waiting forever. An unanswered probe resolves and
   * the session connects with unknown capability; the host applies its own fail-closed
   * policy. See Phase 1 of docs/feature-plans/webrtcfixes13.
   */
  public async connectAsync(
    hostPeerId: string,
    iceServers: RTCIceServer[],
    metadata: PeerMetadata,
    connectOptions?: GuestConnectOptions,
  ): Promise<void> {
    try {
      await this.mediaPolicy.initialize();
    } catch (error) {
      // NO_MUTUAL_HARDWARE_VIDEO_CODEC and any other readiness refusal are terminal: the
      // same page would fail identically on every retry, so surface it rather than retry.
      console.error('[ClientPeerManager] Media policy unavailable:', error);
      this.callbacks.onDisconnected('player:disconnect:no_hardware_codec');
      return;
    }
    return this.session.connect(hostPeerId, iceServers, this.mediaPolicy.advertise(metadata), connectOptions);
  }

  public get hasDecodedFrame(): boolean {
    return this.session.hasDecodedFrame;
  }

  public async handleRemoteSdp(sdp: RTCSessionDescriptionInit, generation?: SignalingGenerationV1): Promise<void> {
    return this.session.handleRemoteSdp(sdp, generation);
  }

  public handleRemoteIceCandidate(candidate: RTCIceCandidate, generation?: SignalingGenerationV1): Promise<void> {
    return this.session.handleRemoteIceCandidate(candidate, generation);
  }

  public send(data: string | ArrayBuffer, label?: string): boolean {
    return this.session.send(data, label);
  }

  public close(reason: string): void {
    this.session.close(reason);
  }

  public isConnected(): boolean {
    return this.session.isConnected();
  }

  public isReconnectInProgress(): boolean {
    return this.session.isReconnectInProgress();
  }

  public getRetryAttempt(): number {
    return this.session.getRetryAttempt();
  }

  public resetRetries(): void {
    this.session.resetRetries();
  }

  /**
   * Milliseconds left in the current recovery episode; 0 once spent, the full budget when no
   * episode is running. App layers read this so every one of them honours the SAME ten
   * seconds instead of each starting a fresh budget on top of the last.
   */
  public recoveryBudgetRemainingMs(): number {
    return this.session.recoveryBudgetRemainingMs();
  }

  /** End the episode, so a refresh gets a full budget rather than inheriting a spent one. */
  public resetRecoveryEpisode(): void {
    this.session.resetRecoveryEpisode();
  }

  public injectCongestion(level: BeaconE2eCongestionLevel): void {
    this.session.injectCongestion(level);
  }

  public getBeaconE2eSnapshot(): BeaconE2eSnapshot {
    return this.session.getBeaconE2eSnapshot();
  }

  /** Subscribe to guest stats samples for telemetry. Replaces any previous listener. */
  public onStatsSample(listener: ((sample: GuestStatsSample) => void) | undefined): void {
    this.session.onStatsSample(listener);
  }

  /** Ask the host for a keyframe. Returns false when it could not be sent. */
  public requestKeyframe(): boolean {
    return this.session.requestKeyframe();
  }

  /** Restart ICE in place. Resolves without throwing when there is no live connection. */
  public restartIce(): Promise<void> {
    return this.session.restartIce();
  }

  /**
   * Hand the remote `<video>` to the session so frame-presentation times can be measured.
   *
   * Without this the guest never registers `requestVideoFrameCallback`, which is the only
   * producer of the RTP-keyed decode hints the calibrated latency path consumes — so the
   * host reports `calibratedLatency: null` for the life of the session. Idempotent; pass
   * null on teardown.
   */
  public attachRemoteVideo(video: RvfcVideoElement | null): void {
    this.session.attachRemoteVideo(video);
  }

  /**
   * Bind the player's canvas surface for WebCodecs playback. Native C++ RTP video needs no
   * surface; without this (and a worker factory) the hello advertises no WebCodecs decode.
   */
  public attachVideoSurface(target: VideoPresentationTarget): () => void {
    return this.session.attachVideoSurface(target);
  }

  public getPresentationEvidence(): ReturnType<GuestPeerSession['getPresentationEvidence']> {
    return this.session.getPresentationEvidence();
  }

  public getRecoveryActionArbiter(): ReturnType<GuestPeerSession['getRecoveryActionArbiter']> {
    return this.session.getRecoveryActionArbiter();
  }

  /**
   * Claim a sequence number for an input about to be sent, or null when this one falls
   * inside the sampling interval and should go out untagged.
   */
  public sampleInputForPhoton(nowMs?: number): number | null {
    return this.session.sampleInputForPhoton(nowMs);
  }

  /** Rolling input-to-photon latency: input event to the frame that could answer it. */
  public inputPhotonSummary(): InputPhotonSummary {
    return this.session.inputPhotonSummary();
  }

  /** Guest video path for debug overlay (`formatVideoPathDebugLine`). */
  public getVideoPathStatus(): ReturnType<GuestPeerSession['getVideoPathStatus']> {
    return this.session.getVideoPathStatus();
  }
}
